import { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useDropzone } from 'react-dropzone';
import PlacesAutocomplete from 'react-places-autocomplete';
import { requestPublicPreviewUrl, requestPublicUploadUrl, validatePublicIncidentReport } from '../Generate/Components/FormikHelpers';
import Select from 'react-select';
import SignatureCanvas from 'react-signature-canvas';

const MAX_FILE_SIZE = 120 * 1024 * 1024;

const BLOCKED_EXTENSIONS = new Set([
  'exe', 'bat', 'cmd', 'com', 'scr', 'pif', 'msi', 'msp', 'cpl', 'dll', 'sys',
  'vbs', 'vbe', 'vb', 'js', 'jse', 'jar', 'wsf', 'wsh', 'ps1', 'psm1', 'sh',
  'htm', 'html', 'mhtml', 'xhtml', 'svg',
  'php', 'phtml', 'php3', 'php4', 'php5', 'phps',
  'asp', 'aspx', 'jsp', 'jspx', 'cgi', 'py', 'pyc', 'pyo', 'rb', 'pl',
  'reg', 'lnk', 'inf', 'apk', 'deb', 'rpm',
]);
const BLOCKED_MIME_TYPES = new Set([
  'text/html', 'application/xhtml+xml',
  'application/javascript', 'text/javascript', 'application/x-javascript',
  'image/svg+xml',
  'application/x-msdownload', 'application/x-msdos-program', 'application/x-msi',
  'application/x-sh', 'application/x-shellscript', 'application/x-bat',
  'application/java-archive', 'application/x-httpd-php', 'application/x-php', 'text/x-php',
  'application/vnd.microsoft.portable-executable',
]);
const isFileTypeAllowed = (fileName, mimeType) => {
  const ext = (typeof fileName === 'string' ? fileName.split('.').pop() : '').toLowerCase();
  if (ext && BLOCKED_EXTENSIONS.has(ext)) return false;
  if (mimeType && BLOCKED_MIME_TYPES.has(mimeType.toLowerCase())) return false;
  return true;
};

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const h = String(i + 1);
  return { value: h, label: h };
});
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => {
  const m = String(i).padStart(2, '0');
  return { value: m, label: m };
});
const AMPM_OPTIONS = [{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }];

const PERSON_TYPE_LABELS = { employee: 'Employee', tenant: 'Tenant', contractor: 'Contractor', other: 'Other' };
const formatLabel = (value) => {
  if (!value) return null;
  return value.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const dtpSelectStyles = (disabled) => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 8,
    borderColor: state.isFocused ? '#163b5f' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(22,59,95,0.15)' : 'none',
    background: disabled ? '#f8fafc' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    transition: 'all 0.2s',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 10px', height: 38 }),
  singleValue: (base) => ({ ...base, color: '#1e293b', fontSize: 13, fontWeight: 500 }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 13 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#94a3b8' }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 9999, borderRadius: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0' }),
  option: (base, state) => ({
    ...base,
    padding: '8px 12px',
    borderRadius: 6,
    margin: '2px 4px',
    width: 'calc(100% - 8px)',
    background: state.isSelected ? 'linear-gradient(135deg,#163b5f,#3a6b98)' : state.isFocused ? '#eef3f7' : '#fff',
    color: state.isSelected ? '#fff' : '#1e293b',
    cursor: 'pointer',
    fontWeight: state.isSelected ? 600 : 400,
  }),
  menuList: (base) => ({ ...base, maxHeight: 180, padding: 4 }),
});


const DateTimePicker = ({ value, onChange, disabled, maxNow = false }) => {
  const pad = (n) => String(n).padStart(2, '0');
  const parseValue = (v) => {
    if (!v) return { date: '', hours: null, minutes: null, ampm: null };
    const d = new Date(v);
    if (isNaN(d.getTime())) return { date: '', hours: null, minutes: null, ampm: null };
    const h24 = d.getHours();
    const ap = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      hours: { value: String(h12), label: String(h12) },
      minutes: { value: pad(d.getMinutes()), label: pad(d.getMinutes()) },
      ampm: { value: ap, label: ap },
    };
  };
  const parsed = parseValue(value);
  const [localDate, setLocalDate] = useState(parsed.date);
  const [localHours, setLocalHours] = useState(parsed.hours);
  const [localMinutes, setLocalMinutes] = useState(parsed.minutes);
  const [localAmpm, setLocalAmpm] = useState(parsed.ampm);

  const buildAndEmit = (d, h, m, ap) => {
    if (!d || !h || !m || !ap) return;
    const [year, month, day] = d.split('-').map(Number);
    let h24 = parseInt(h, 10) % 12;
    if (ap === 'PM') h24 += 12;
    const result = new Date(year, month - 1, day, h24, parseInt(m, 10), 0, 0);
    if (!isNaN(result.getTime())) onChange(result);
  };
  const handleDate = (v) => { setLocalDate(v); buildAndEmit(v, localHours?.value, localMinutes?.value, localAmpm?.value); };
  const handleHours = (o) => { setLocalHours(o); buildAndEmit(localDate, o?.value, localMinutes?.value, localAmpm?.value); };
  const handleMinutes = (o) => { setLocalMinutes(o); buildAndEmit(localDate, localHours?.value, o?.value, localAmpm?.value); };
  const handleAmpm = (o) => { setLocalAmpm(o); buildAndEmit(localDate, localHours?.value, localMinutes?.value, o?.value); };
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="date"
        className="pir-input"
        style={{ flex: '2 1 0', minWidth: 0 }}
        value={localDate}
        max={maxNow ? todayStr : undefined}
        disabled={disabled}
        onChange={(e) => handleDate(e.target.value)}
      />
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <Select options={HOUR_OPTIONS} value={localHours} isDisabled={disabled} onChange={handleHours} placeholder="HH" menuPortalTarget={document.body} menuPosition="fixed" styles={dtpSelectStyles(disabled)} isSearchable={false} />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <Select options={MINUTE_OPTIONS} value={localMinutes} isDisabled={disabled} onChange={handleMinutes} placeholder="MM" menuPortalTarget={document.body} menuPosition="fixed" styles={dtpSelectStyles(disabled)} isSearchable={false} />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <Select options={AMPM_OPTIONS} value={localAmpm} isDisabled={disabled} onChange={handleAmpm} placeholder="AM" menuPortalTarget={document.body} menuPosition="fixed" styles={dtpSelectStyles(disabled)} isSearchable={false} />
      </div>
    </div>
  );
};

const AddressInput = ({ value, onChange, disabled, placeholder = 'Search address…', className = 'pir-input' }) => {
  const wrapperRef = useRef(null);

  const getDropdownStyle = () => {
    if (!wrapperRef.current) return {};
    const rect = wrapperRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      background: '#fff',
      border: '1.5px solid rgba(22,59,95,0.15)',
      borderRadius: 12,
      boxShadow: '0 12px 40px rgba(6,21,36,0.25)',
      maxHeight: 250,
      overflowY: 'auto',
    };
  };

  if (disabled) return <input className={className} placeholder={placeholder} value={value || ''} disabled />;

  return (
    <PlacesAutocomplete value={value || ''} onChange={onChange} onSelect={onChange} searchOptions={{ componentRestrictions: { country: ['US', 'CA'] } }}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
          <input {...getInputProps({ className, placeholder, style: { width: '100%' } })} />
          {(loading || suggestions.length > 0) && ReactDOM.createPortal(
            <div style={getDropdownStyle()}>
              {loading && <div style={{ padding: '10px 14px', fontSize: 13, color: '#6a8ca8' }}>Loading suggestions…</div>}
              {suggestions.map((s) => (
                <div {...getSuggestionItemProps(s)} key={s.placeId} style={{
                  padding: '11px 14px',
                  fontSize: 13.5,
                  cursor: 'pointer',
                  background: s.active ? 'rgba(22,59,95,0.1)' : '#fff',
                  color: s.active ? '#163b5f' : '#061524',
                  borderBottom: '1px solid rgba(22,59,95,0.07)',
                  transition: 'background 0.1s',
                }}>
                  {s.description}
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

const FL = ({ children, required, hint }) => (
  <label className="pir-field-label">
    {children}
    {required && <span className="pir-required"> *</span>}
    {hint && (
      <span className="pir-hint-wrap">
        <svg className="pir-hint-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span className="pir-hint-tooltip">{hint}</span>
      </span>
    )}
  </label>
);

const WitnessBlock = ({ w, idx, fieldPrefix, disabled, formik }) => (
  <div style={{ background: 'rgba(22,59,95,0.03)', border: '1.5px solid rgba(22,59,95,0.1)', borderRadius: 10, padding: '14px 16px', marginBottom: 8 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 10 }}>
      <div>
        <FL required>First Name</FL>
        <input className="pir-input" placeholder="First name" value={w.firstName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].firstName`, e.target.value)} />
      </div>
      <div>
        <FL>Middle Name</FL>
        <input className="pir-input" placeholder="Middle name" value={w.middleName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].middleName`, e.target.value)} />
      </div>
      <div>
        <FL required>Last Name</FL>
        <input className="pir-input" placeholder="Last name" value={w.lastName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].lastName`, e.target.value)} />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
      <div style={{ gridColumn: 'span 1' }}>
        <FL required>Address</FL>
        <AddressInput placeholder="Address" value={w.address || ''} disabled={disabled}
          onChange={(val) => formik.setFieldValue(`${fieldPrefix}[${idx}].address`, val)} />
      </div>
      <div>
        <FL>Phone</FL>
        <input className="pir-input" placeholder="Phone number" value={w.phoneNo || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].phoneNo`, e.target.value)} />
      </div>
      <div>
        <FL>Email</FL>
        <input className="pir-input" type="email" placeholder="email@example.com" value={w.email || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].email`, e.target.value)} />
      </div>
    </div>
  </div>
);


const MediaPreviewModal = ({ isOpen, toggle, media, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onPrev, onNext]);

  if (!media) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static" style={{ zIndex: 9999 }}>
      <ModalHeader toggle={toggle} style={{ background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)', border: 'none', borderRadius: '12px 12px 0 0' }}>
        <span style={{ color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
          {media.isVideo ? '🎬 Video' : media.isPdf ? '📄 Document' : '🖼 Image'} — {media.name || 'Preview'}
        </span>
      </ModalHeader>
      <ModalBody className="text-center py-4" style={{ background: '#F8FAFC', position: 'relative' }}>
        {onPrev && (
          <button onClick={onPrev} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <FeatherIcon icon="chevron-left" />
          </button>
        )}
        {media.isVideo
          ? <video src={media.url} controls autoPlay style={{ maxHeight: '75vh', maxWidth: '100%', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} />
          : media.isPdf
            ? <iframe src={media.url} style={{ width: '100%', height: '75vh', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} title="Document Preview" />
            : <img src={media.url} alt={media.name || 'Preview'} style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} />
        }
        {onNext && (
          <button onClick={onNext} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <FeatherIcon icon="chevron-right" />
          </button>
        )}
      </ModalBody>
      <ModalFooter style={{ background: '#ffffff', borderTop: '1px solid #E2E8F0', justifyContent: 'center', borderRadius: '0 0 12px 12px' }}>
        <Button onClick={toggle} style={{ background: '#eef3f7', border: '1px solid #c2d5e3', color: '#061524', borderRadius: 8, padding: '8px 20px', fontWeight: 600 }}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};


const SignaturePad = ({ value, date, onChange, disabled, onDateChange }) => {
  const sigRef = useRef(null);
  const [sigModal, setSigModal] = useState(false);

  const handleClear = () => sigRef.current?.clear();

  const handleSave = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const signatureData = sigRef.current.toDataURL('image/png');
      const today = new Date().toISOString().split('T')[0];
      onDateChange?.(today);
      onChange(signatureData);
      setSigModal(false);
    } else {
      toast.warning('Please sign before saving.');
    }
  };

  const handleRemove = async () => {
    const r = await Swal.fire({
      title: 'Remove Signature?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#163b5f',
      confirmButtonText: 'Remove',
    });
    if (r.isConfirmed) { onChange(''); onDateChange?.(''); }
  };

  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  return (
    <>
      {value ? (
        <div style={{ border: '1.5px solid #c2d5e3', borderRadius: 12, padding: 12, background: 'linear-gradient(135deg,#eef3f7,#dbe6f0)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 6, border: '1px solid #a6c4da' }}>
            <img src={value} alt="Signature" style={{ height: 44, maxWidth: 180, objectFit: 'contain' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FeatherIcon icon="check-circle" size={13} /> Signed
            </div>
            {formattedDate && <div style={{ fontSize: 11, color: '#163b5f', marginTop: 2, fontWeight: 500 }}>Signed on {formattedDate}</div>}
          </div>
          {!disabled && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="pir-btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setSigModal(true)}>Re-sign</button>
              <button type="button" className="pir-btn-danger-sm" onClick={handleRemove}><FeatherIcon icon="trash-2" size={12} /></button>
            </div>
          )}
        </div>
      ) : disabled ? (
        <div className="pir-empty-msg" style={{ padding: '8px 12px', fontSize: 12 }}>
          <FeatherIcon icon="edit-3" size={13} color="#94a3b8" /><span>No signature provided</span>
        </div>
      ) : (
        <button type="button" className="pir-btn-sign" onClick={() => setSigModal(true)}>
          <FeatherIcon icon="edit-3" size={15} />
          <span>Click to Add Signature</span>
          <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.7 }}>Required</span>
        </button>
      )}

      <Modal isOpen={sigModal} toggle={() => setSigModal(false)} centered size="lg" backdrop="static">
        <ModalHeader toggle={() => setSigModal(false)} style={{ background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)', border: 'none', borderRadius: '12px 12px 0 0' }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>✍️ Draw Your Signature</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <div style={{ border: '2px dashed #a6c4da', borderRadius: 12, background: 'linear-gradient(135deg,#fafafa,#eef3f7)', position: 'relative', overflow: 'hidden' }}>
            <SignatureCanvas
              ref={sigRef}
              penColor="#0F172A"
              canvasProps={{ style: { width: '100%', height: 200, borderRadius: 12, display: 'block' } }}
            />
            <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 11, color: '#6a8ca8', pointerEvents: 'none', fontWeight: 500 }}>
              ✦ Draw your signature in the area above ✦
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, textAlign: 'center' }}>
            By signing, you confirm all information provided is true and accurate to the best of your knowledge.
          </p>
        </ModalBody>
        <ModalFooter style={{ border: 'none', paddingTop: 0, gap: 10 }}>
          <button type="button" className="pir-btn-ghost" onClick={handleClear}><FeatherIcon icon="refresh-cw" size={13} /> Clear</button>
          <button type="button" className="pir-btn-primary-violet" onClick={handleSave}><FeatherIcon icon="check" size={13} /> Save Signature</button>
        </ModalFooter>
      </Modal>
    </>
  );
};


/* ─────────────────────────────────────────────────────────────
   AttachmentsRow — with mandatory Yes / No choice gate
   attachmentChoice: null | 'yes' | 'no'  (stored in formik)
───────────────────────────────────────────────────────────── */
const AttachmentsRow = ({ formik, token, mode, field, isRequired = false, docType = null, onUploadingChange, choiceField }) => {
  const [pendingUploads, setPendingUploads] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const uploadedKeysRef = useRef(null);

  const adjustActive = useCallback((delta) => { onUploadingChange?.(delta); }, [onUploadingChange]);

  const getNested = (obj, path) => path.split('.').reduce((a, k) => {
    if (a == null) return undefined;
    const arrMatch = k.match(/^(\w+)\[(\d+)\]$/);
    if (arrMatch) return a[arrMatch[1]]?.[parseInt(arrMatch[2])];
    return a[k];
  }, obj);

  const attachments = getNested(formik.values, field) || (isRequired ? '' : []);
  // attachmentChoice: null = not chosen yet, 'yes' = upload, 'no' = explicitly skipped
  const attachmentChoice = choiceField ? getNested(formik.values, choiceField) : undefined;
  const editable = (field.startsWith('notification') && mode === 'initial') || (field.startsWith('investigation') && mode === 'formal');

  const processFiles = useCallback(async (files) => {
    if (!files?.length) return;
    if (!token) { toast.error('Missing token — cannot upload.'); return; }
    const valid = [], invalid = [];
    files.forEach((f) => {
      if (f.size > MAX_FILE_SIZE) invalid.push({ f, err: 'size' });
      else if (!isFileTypeAllowed(f.name, f.type)) invalid.push({ f, err: 'type' });
      else valid.push(f);
    });
    invalid.forEach(({ f, err }) => toast.error(err === 'size' ? `${f.name} exceeds 120 MB` : `${f.name}: this file type is not allowed for security reasons`));
    if (!valid.length) return;
    const pending = valid.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f, preview: URL.createObjectURL(f),
      name: f.name, type: f.type, size: f.size, progress: 0, uploading: false,
    }));
    setPendingUploads((prev) => [...prev, ...pending]);
    pending.forEach((item) => uploadFile(item));
  }, [token, field, formik]);

  const { getRootProps, getInputProps, isDragActive, open: openDropzone } = useDropzone({
    onDrop: (acc, rej) => {
      rej.forEach(({ file, errors }) => errors.forEach((e) => {
        if (e.code === 'file-too-large') toast.error(`${file.name} is too large`);
        if (e.code === 'file-invalid-type') toast.error(`${file.name}: this file type is not allowed for security reasons`);
      }));
      processFiles(acc);
    },
    multiple: !isRequired, maxSize: MAX_FILE_SIZE, noClick: true, noKeyboard: true,
  });

  const uploadFile = async (item) => {
    const updById = (patch) => setPendingUploads((p) => p.map((u) => (u.id === item.id ? { ...u, ...patch } : u)));
    const removeById = () => setPendingUploads((p) => {
      const target = p.find((u) => u.id === item.id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return p.filter((u) => u.id !== item.id);
    });
    updById({ uploading: true });
    adjustActive(1);
    try {
      const { presignedUrl, key } = await requestPublicUploadUrl({ token, fileName: item.file.name, contentType: item.file.type });
      updById({ progress: 10 });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', item.file.type);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) updById({ progress: 10 + Math.round((e.loaded / e.total) * 80) }); };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(item.file);
      });
      updById({ progress: 100 });
      if (isRequired) {
        formik.setFieldValue(field, key);
      } else {
        if (!uploadedKeysRef.current) {
          const cur = getNested(formik.values, field);
          uploadedKeysRef.current = Array.isArray(cur) ? [...cur] : [];
        }
        if (!uploadedKeysRef.current.includes(key)) uploadedKeysRef.current = [...uploadedKeysRef.current, key];
        formik.setFieldValue(field, uploadedKeysRef.current);
      }
      adjustActive(-1);
      setTimeout(removeById, 1500);
    } catch (err) {
      toast.error(err.message || `Failed to upload "${item.file.name}"`);
      adjustActive(-1);
      removeById();
    }
  };

  const removePending = (id) => setPendingUploads((p) => {
    const item = p.find((u) => u.id === id);
    if (item?.preview) URL.revokeObjectURL(item.preview);
    return p.filter((u) => u.id !== id);
  });

  const removeUploaded = async (idx = null) => {
    const r = await Swal.fire({ title: 'Remove Attachment?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', cancelButtonColor: '#163b5f', confirmButtonText: 'Remove' });
    if (r.isConfirmed) {
      if (isRequired) {
        formik.setFieldValue(field, '');
        uploadedKeysRef.current = [];
      } else {
        const cur = [...(getNested(formik.values, field) || [])];
        cur.splice(idx, 1);
        uploadedKeysRef.current = [...cur];
        formik.setFieldValue(field, cur);
        // If all files removed, reset choice so user must decide again
        if (cur.length === 0 && choiceField) {
          formik.setFieldValue(choiceField, null);
        }
      }
      toast.success('Attachment removed');
    }
  };

  const previewUploaded = async (key, name, isVideo, isPdf, index) => {
    if (!token) { toast.error('Missing token — cannot preview.'); return; }
    try {
      const data = await requestPublicPreviewUrl({ token, key });
      setSelectedMedia({ url: data.presignedUrl, name, isVideo, isPdf, index });
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
    }
  };

  const fileName = (k) => (typeof k === 'string' ? k.split('/').pop() : '');

  const navigateMedia = (delta) => {
    if (!selectedMedia || selectedMedia.index === undefined) return;
    const list = isRequired ? (attachments ? [attachments] : []) : (attachments || []);
    const newIdx = selectedMedia.index + delta;
    if (newIdx >= 0 && newIdx < list.length) {
      const key = list[newIdx];
      previewUploaded(key, fileName(key), isVideoKey(key), isPdfKey(key), newIdx);
    }
  };
  const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
  const isPdfKey = (k) => /\.pdf$/i.test(k);
  const isImageKey = (k) => /\.(jpe?g|png|gif|webp)$/i.test(k);

  const UploadedRow = ({ fileKey, onPreview, onRemove }) => {
    const isImage = isImageKey(fileKey);
    const [thumbUrl, setThumbUrl] = useState(null);

    useEffect(() => {
      if (!isImage || !token || !fileKey) return;
      requestPublicPreviewUrl({ token, key: fileKey })
        .then((data) => setThumbUrl(data.presignedUrl))
        .catch(() => { });
    }, [fileKey, isImage]);

    return (
      <div className="pir-file-row pir-file-done" style={{ cursor: 'pointer' }} onClick={onPreview}>
        <div className="pir-file-thumb pir-file-thumb-done">
          {isImage ? (
            thumbUrl ? (
              <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Spinner size="sm" color="secondary" />
              </div>
            )
          ) : (
            <FeatherIcon icon={isPdfKey(fileKey) ? 'file-text' : isVideoKey(fileKey) ? 'video' : 'image'} size={14} color="#163b5f" />
          )}
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="pir-file-name">{fileName(fileKey)}</div>
          <div className="pir-file-meta" style={{ color: '#163b5f' }}>
            <FeatherIcon icon="check-circle" size={10} className="me-1" />
            {isPdfKey(fileKey) ? 'Document' : isVideoKey(fileKey) ? 'Video' : 'Image'} · Uploaded
          </div>
        </div>
        {editable && (
          <button className="pir-icon-btn pir-icon-btn-danger" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <FeatherIcon icon="trash-2" size={13} />
          </button>
        )}
      </div>
    );
  };

  // ── Read-only / preview mode ──
  if (!editable) {
    return (
      <>
        <div className="d-flex flex-column gap-1 mb-2">
          {isRequired
            ? attachments && <UploadedRow fileKey={attachments} onPreview={() => previewUploaded(attachments, fileName(attachments), isVideoKey(attachments), isPdfKey(attachments), 0)} onRemove={() => { }} />
            : (attachments || []).map((key, idx) => <UploadedRow key={`u-${idx}`} fileKey={key} onPreview={() => previewUploaded(key, fileName(key), isVideoKey(key), isPdfKey(key), idx)} onRemove={() => { }} />)
          }
        </div>
        {(isRequired ? !attachments : (attachments || []).length === 0) && (
          <div className="pir-empty-msg">
            <FeatherIcon icon="file" size={13} color="#94a3b8" />
            <span>No {isRequired ? `${docType} document` : 'attachments'} uploaded</span>
          </div>
        )}
        <MediaPreviewModal
          isOpen={!!selectedMedia}
          toggle={() => setSelectedMedia(null)}
          media={selectedMedia}
          onPrev={selectedMedia?.index > 0 ? () => navigateMedia(-1) : null}
          onNext={selectedMedia && selectedMedia.index < (isRequired ? (attachments ? 1 : 0) : (attachments || []).length) - 1 ? () => navigateMedia(1) : null}
        />
      </>
    );
  }

  // ── Editable mode: simple required file (no choice gate) ──
  if (isRequired) {
    return (
      <>
        <div className="d-flex flex-column gap-1 mb-2">
          {pendingUploads.map((m) => (
            <div key={m.id} className="pir-file-row">
              <div className="pir-file-thumb">
                {m.type?.startsWith('image/') && m.preview
                  ? <img src={m.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                  : <FeatherIcon icon={m.type === 'application/pdf' ? 'file-text' : 'video'} size={14} color="#94a3b8" />
                }
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="pir-file-name">{m.name}</div>
                <div className="pir-file-meta">{(m.size / 1024 / 1024).toFixed(1)} MB{m.uploading && ` · ${m.progress}%`}</div>
                {m.uploading && <div className="pir-prog-bar"><div className="pir-prog-fill" style={{ width: `${m.progress}%` }} /></div>}
              </div>
              {!m.uploading && <button className="pir-icon-btn" onClick={(e) => { e.stopPropagation(); removePending(m.id); }}><FeatherIcon icon="x" size={12} /></button>}
            </div>
          ))}
          {attachments && <UploadedRow fileKey={attachments} onPreview={() => previewUploaded(attachments, fileName(attachments), isVideoKey(attachments), isPdfKey(attachments), 0)} onRemove={() => removeUploaded()} />}
        </div>
        {!attachments && (
          <div {...getRootProps()} onClick={openDropzone} className={`pir-dropzone-sm ${isDragActive ? 'active' : ''}`}>
            <input {...getInputProps()} />
            <FeatherIcon icon="upload-cloud" size={15} />
            <span>{isDragActive ? 'Drop here' : `Upload ${docType || 'file'} — Any file · Max 120 MB`}</span>
          </div>
        )}
        <MediaPreviewModal
          isOpen={!!selectedMedia}
          toggle={() => setSelectedMedia(null)}
          media={selectedMedia}
          onPrev={selectedMedia?.index > 0 ? () => navigateMedia(-1) : null}
          onNext={selectedMedia && selectedMedia.index < (attachments ? 1 : 0) - 1 ? () => navigateMedia(1) : null}
        />
      </>
    );
  }

  // ── Editable mode: optional attachments WITH mandatory choice gate ──
  const uploadedCount = Array.isArray(attachments) ? attachments.length : 0;
  const hasUploads = uploadedCount > 0 || pendingUploads.length > 0;

  // Choice not yet made — show mandatory Yes/No prompt
  if (attachmentChoice === null || attachmentChoice === undefined) {
    return (
      <>
        <div style={{
          border: '2px dashed #c2d5e3',
          borderRadius: 14,
          background: 'linear-gradient(135deg,#f8fafc,#eef3f7)',
          padding: '20px 22px',
          textAlign: 'center',
        }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', boxShadow: '0 4px 14px rgba(22,59,95,0.3)' }}>
            <FeatherIcon icon="paperclip" size={20} color="#fff" />
          </div>
          <p style={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Do you have any files to attach?</p>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 18px' }}>Photos, videos, or documents related to the incident</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => {
                if (choiceField) formik.setFieldValue(choiceField, 'yes');
                setTimeout(() => openDropzone(), 100);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 22px', borderRadius: 9,
                background: 'linear-gradient(135deg,#163b5f,#3a6b98)',
                color: '#fff', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                boxShadow: '0 4px 12px rgba(22,59,95,0.3)',
                transition: 'all 0.18s',
              }}
            >
              <FeatherIcon icon="upload" size={14} />
              Yes — Upload Files
            </button>
            <button
              type="button"
              onClick={() => {
                if (choiceField) formik.setFieldValue(choiceField, 'no');
                formik.setFieldValue(field, []);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 22px', borderRadius: 9,
                background: '#fff', color: '#475569',
                border: '1.5px solid #cbd5e1', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                transition: 'all 0.18s',
              }}
            >
              <FeatherIcon icon="x-circle" size={14} />
              No Attachments
            </button>
          </div>
        </div>
        <MediaPreviewModal isOpen={!!selectedMedia} toggle={() => setSelectedMedia(null)} media={selectedMedia} />
      </>
    );
  }

  // Choice = 'no' — show confirmed no-attachment state with option to change
  if (attachmentChoice === 'no') {
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px',
          border: '1.5px solid #e2e8f0',
          borderRadius: 11,
          background: '#f8fafc',
        }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1.5px solid #e2e8f0' }}>
            <FeatherIcon icon="paperclip" size={15} color="#94a3b8" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>No attachments</div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 1 }}>You indicated there are no files to attach</div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (choiceField) formik.setFieldValue(choiceField, null);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 13px', borderRadius: 8,
              background: '#eef3f7', color: '#163b5f',
              border: '1.5px solid #c2d5e3', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, flexShrink: 0,
            }}
          >
            <FeatherIcon icon="refresh-cw" size={12} />
            Change
          </button>
        </div>
        <MediaPreviewModal isOpen={!!selectedMedia} toggle={() => setSelectedMedia(null)} media={selectedMedia} />
      </>
    );
  }

  // Choice = 'yes' — show upload UI
  return (
    <>
      <div className="d-flex flex-column gap-1 mb-2">
        {pendingUploads.map((m) => (
          <div key={m.id} className="pir-file-row">
            <div className="pir-file-thumb">
              {m.type?.startsWith('image/') && m.preview
                ? <img src={m.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                : <FeatherIcon icon={m.type === 'application/pdf' ? 'file-text' : 'video'} size={14} color="#94a3b8" />
              }
            </div>
            <div className="flex-grow-1 min-w-0">
              <div className="pir-file-name">{m.name}</div>
              <div className="pir-file-meta">{(m.size / 1024 / 1024).toFixed(1)} MB{m.uploading && ` · ${m.progress}%`}</div>
              {m.uploading && <div className="pir-prog-bar"><div className="pir-prog-fill" style={{ width: `${m.progress}%` }} /></div>}
            </div>
            {!m.uploading && <button className="pir-icon-btn" onClick={(e) => { e.stopPropagation(); removePending(m.id); }}><FeatherIcon icon="x" size={12} /></button>}
          </div>
        ))}
        {(attachments || []).map((key, idx) => (
          <UploadedRow key={`u-${idx}`} fileKey={key} onPreview={() => previewUploaded(key, fileName(key), isVideoKey(key), isPdfKey(key), idx)} onRemove={() => removeUploaded(idx)} />
        ))}
      </div>

      {/* Dropzone */}
      {hasUploads ? (
        <div {...getRootProps()} onClick={openDropzone} className={`pir-dropzone-sm ${isDragActive ? 'active' : ''}`} style={{ marginTop: 6 }}>
          <input {...getInputProps()} />
          <FeatherIcon icon="plus-circle" size={14} />
          <span>{isDragActive ? 'Drop to add' : 'Add more files'}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>Any file · Max 120 MB</span>
        </div>
      ) : (
        <div {...getRootProps()} onClick={openDropzone} className={`pir-dropzone-multi ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <FeatherIcon icon="upload-cloud" size={28} color={isDragActive ? '#163b5f' : '#94a3b8'} />
          <span style={{ fontWeight: 600, fontSize: 13, color: isDragActive ? '#163b5f' : '#374151' }}>{isDragActive ? 'Drop files here' : 'Drag & drop or click to browse'}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Images, videos, documents & more · Max 120 MB</span>
        </div>
      )}

      {/* Allow changing mind */}
      <button
        type="button"
        style={{
          marginTop: 8,
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          fontSize: 12, color: '#475569',
          background: '#f1f5f9', border: '1.5px solid #cbd5e1', borderRadius: 8,
          padding: '7px 14px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
        onClick={() => {
          if (choiceField) formik.setFieldValue(choiceField, null);
          formik.setFieldValue(field, []);
        }}
      >
        <FeatherIcon icon="x-circle" size={13} />
        Actually, I have no files to attach
      </button>

      <MediaPreviewModal
        isOpen={!!selectedMedia}
        toggle={() => setSelectedMedia(null)}
        media={selectedMedia}
        onPrev={selectedMedia?.index > 0 ? () => navigateMedia(-1) : null}
        onNext={selectedMedia && selectedMedia.list && selectedMedia.index < selectedMedia.list.length - 1 ? () => navigateMedia(1) : null}
      />
    </>
  );
};


const AttachmentThumbnail = ({ fileKey, token, onClick }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const isImageKey = (k) => /\.(jpe?g|png|gif|webp)$/i.test(k);
  const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
  const isPdfKey = (k) => /\.pdf$/i.test(k);
  const isImage = isImageKey(fileKey);

  useEffect(() => {
    if (!isImage || !token || !fileKey) return;
    requestPublicPreviewUrl({ token, key: fileKey })
      .then((data) => setThumbUrl(data.presignedUrl))
      .catch(() => { });
  }, [isImage, token, fileKey]);

  return (
    <div onClick={onClick} style={{ width: 88, height: 88, borderRadius: 10, border: '2px solid #c2d5e3', overflow: 'hidden', cursor: 'pointer', background: '#eef3f7', position: 'relative', flexShrink: 0, transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(22,59,95,0.1)' }}>
      {isImage ? (
        thumbUrl ? (
          <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spinner size="sm" color="secondary" />
          </div>
        )
      ) : isVideoKey(fileKey) ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FeatherIcon icon="video" size={26} color="#163b5f" />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FeatherIcon icon="file-text" size={26} color="#163b5f" />
        </div>
      )}
    </div>
  );
};


const PreviewField = ({ label, value, wide, full }) => {
  const isEmpty = !value || value === '—';
  return (
    <div style={{
      gridColumn: full ? '1 / -1' : wide ? 'span 2' : 'span 1',
      background: '#fff',
      border: '1px solid #dbe6f0',
      borderRadius: 10,
      padding: '12px 16px',
      minHeight: 56,
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6a8ca8', marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: isEmpty ? 400 : 500, color: isEmpty ? '#a6c4da' : '#0F172A', lineHeight: 1.5, whiteSpace: full ? 'pre-wrap' : 'normal' }}>
        {isEmpty ? '—' : value}
      </div>
    </div>
  );
};


const PreviewSection = ({ icon, title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #dbe6f0', marginBottom: 14 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(22,59,95,0.3)' }}>
        <FeatherIcon icon={icon} size={14} color="#fff" />
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: '#163b5f', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
      {children}
    </div>
  </div>
);


const PreviewModal = ({ isOpen, toggle, persons, onConfirmSubmit, isSubmitting, token }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  if (!isOpen) return null;

  const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const display = (v) => (v === null || v === undefined || v === '') ? null : String(v);
  const fileName = (k) => (typeof k === 'string' ? k.split('/').pop() : '');
  const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
  const isPdfKey = (k) => /\.pdf$/i.test(k);

  const previewUploaded = async (key, list, index) => {
    if (!token) { toast.error('Missing token'); return; }
    try {
      const data = await requestPublicPreviewUrl({ token, key });
      setSelectedMedia({ url: data.presignedUrl, name: fileName(key), isVideo: isVideoKey(key), isPdf: isPdfKey(key), list, index });
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
    }
  };

  const navigateMedia = (delta) => {
    if (!selectedMedia || !selectedMedia.list || selectedMedia.index === undefined) return;
    const newIdx = selectedMedia.index + delta;
    if (newIdx >= 0 && newIdx < selectedMedia.list.length) {
      const key = selectedMedia.list[newIdx];
      previewUploaded(key, selectedMedia.list, newIdx);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static" className="pir-preview-modal">
      <ModalHeader toggle={toggle} style={{
        background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)',
        borderRadius: '16px 16px 0 0',
        border: 'none',
        padding: '22px 28px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, background: 'rgba(255,255,255,0.2)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.3)' }}>
            <FeatherIcon icon="eye" size={22} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Review Your Submission</div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', marginTop: 3 }}>Please verify all details before submitting</div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ background: '#eef3f7', padding: '24px', maxHeight: '62vh', overflowY: 'auto' }}>
        {persons.map((p, idx) => {
          const typeDisplay = p.type
            ? (p.type === 'other' && p.specifyType ? `Other (${p.specifyType})` : PERSON_TYPE_LABELS[p.type] || formatLabel(p.type))
            : null;
          return (
            <div key={idx} style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #c2d5e3', marginBottom: idx < persons.length - 1 ? 24 : 0, overflow: 'hidden', boxShadow: '0 4px 20px rgba(22,59,95,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 22px', background: 'linear-gradient(135deg, #0b1d31 0%, #3a6b98 70%, #eef3f7 100%)', borderBottom: 'none', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -30, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.3)', flexShrink: 0, fontSize: 18, color: '#fff', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  {[p.firstName, p.lastName].filter(Boolean).join(' ').charAt(0) || <FeatherIcon icon="user" size={20} color="#fff" />}
                </div>
                <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    {[p.firstName, p.lastName].filter(Boolean).join(' ') || 'Your Details'}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 3 }}>
                    {typeDisplay && <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '2px 10px', borderRadius: 20, marginRight: 8, fontSize: 11, fontWeight: 600 }}>{typeDisplay}</span>}
                    {p.email}
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px 22px' }}>
                <PreviewSection icon="user" title="Your Information">
                  <PreviewField label="First Name" value={display(p.firstName)} />
                  <PreviewField label="Middle Name" value={display(p.middleName)} />
                  <PreviewField label="Last Name" value={display(p.lastName)} />
                  <PreviewField label="Person Type" value={typeDisplay} />
                  <PreviewField label="Email" value={display(p.email)} wide />
                  <PreviewField label="Phone Number(s)" value={display((Array.isArray(p.phoneNumbers) && p.phoneNumbers.filter(Boolean).length > 0) ? p.phoneNumbers.filter(Boolean).join(', ') : p.phoneNo)} />
                  <PreviewField label="Unit No." value={display(p.unitNo)} />
                  <PreviewField label="Home Address" value={display(p.homeAddress || p.address)} wide />
                </PreviewSection>

                <PreviewSection icon="file-text" title="Statement of Incident">
                  <PreviewField label="Notification to Property Manager" value={formatDateTime(p.notificationToPropertyManagerDateTime)} wide />
                  <PreviewField label="Statement" value={display(p.statement)} full />
                </PreviewSection>

                {p.attachments && p.attachments.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #dbe6f0', marginBottom: 14 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(22,59,95,0.3)' }}>
                        <FeatherIcon icon="paperclip" size={14} color="#fff" />
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: '#163b5f', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Attachments ({p.attachments.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {p.attachments.map((key, ai) => (
                        <AttachmentThumbnail key={ai} fileKey={key} token={token} onClick={() => previewUploaded(key, p.attachments, ai)} />
                      ))}
                    </div>
                  </div>
                )}

                {p.witnesses && p.witnesses.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #dbe6f0', marginBottom: 14 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(22,59,95,0.3)' }}>
                        <FeatherIcon icon="users" size={14} color="#fff" />
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: '#163b5f', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Witnesses ({p.witnesses.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {p.witnesses.map((w, wi) => (
                        <div key={wi} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>
                            {[w.firstName, w.lastName].filter(Boolean).join(' ') || `Witness ${wi + 1}`}
                          </div>
                          <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                            {w.email && <span>✉ {w.email}</span>}
                            {w.phoneNo && <span>📞 {w.phoneNo}</span>}
                            {w.address && <span>📍 {w.address}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {p.signature?.signature && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #d1fae5', marginBottom: 14 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(5,150,105,0.3)' }}>
                        <FeatherIcon icon="edit-3" size={14} color="#fff" />
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Signature</span>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 18 }}>
                      <div style={{ background: '#fff', border: '1px solid #a7f3d0', borderRadius: 8, padding: 8 }}>
                        <img src={p.signature.signature} alt="Signature" style={{ height: 52, maxWidth: 220, objectFit: 'contain' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FeatherIcon icon="check-circle" size={15} /> Signed & Verified
                        </div>
                        {p.signature.date && <div style={{ fontSize: 12, color: '#047857', marginTop: 3, fontWeight: 500 }}>{formatDate(p.signature.date)}</div>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </ModalBody>

      <ModalFooter style={{ background: '#fff', borderTop: '1px solid #c2d5e3', padding: '18px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderRadius: '0 0 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#163b5f', fontWeight: 500 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: '#eef3f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FeatherIcon icon="shield" size={13} color="#163b5f" />
          </div>
          By submitting you certify all information is true and accurate.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="pir-btn-ghost" onClick={toggle} disabled={isSubmitting}>
            <FeatherIcon icon="edit-2" size={14} /> Back to Edit
          </button>
          <button
            type="button"
            onClick={onConfirmSubmit}
            disabled={isSubmitting}
            className="pir-submit-btn"
          >
            {isSubmitting ? (
              <><Spinner size="sm" color="light" style={{ marginRight: 8 }} />Submitting Report...</>
            ) : (
              <><FeatherIcon icon="send" size={15} />Confirm & Submit Report</>
            )}
          </button>
        </div>
      </ModalFooter>
      <MediaPreviewModal
        isOpen={!!selectedMedia}
        toggle={() => setSelectedMedia(null)}
        media={selectedMedia}
        onPrev={selectedMedia?.index > 0 ? () => navigateMedia(-1) : null}
        onNext={selectedMedia && selectedMedia.list && selectedMedia.index < selectedMedia.list.length - 1 ? () => navigateMedia(1) : null}
      />
    </Modal>
  );
};


/* ─── Sub-divider used inside Statement of Incident box ─── */
const SubDivider = ({ icon, label, children }) => (
  <div style={{ margin: '20px 0 12px' }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      paddingBottom: 10,
      borderBottom: '1px solid rgba(22,59,95,0.1)',
      marginBottom: 12,
    }}>
      <FeatherIcon icon={icon} size={13} color="#3a6b98" />
      <span style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: '#3a6b98',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>{label}</span>
      {children}
    </div>
  </div>
);


const SectionBox = ({ title, icon, children, accentColor }) => (
  <div style={{
    border: '1.5px solid rgba(22,59,95,0.12)',
    borderRadius: 14,
    marginBottom: 18,
    position: 'relative',
    zIndex: 1,
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '12px 18px',
      background: 'linear-gradient(135deg, rgba(6,21,36,0.04), rgba(22,59,95,0.06))',
      borderBottom: '1.5px solid rgba(22,59,95,0.1)',
    }}>
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: accentColor || 'linear-gradient(135deg,#163b5f,#3a6b98)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 3px 8px rgba(22,59,95,0.25)',
      }}>
        <FeatherIcon icon={icon} size={13} color="#fff" />
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: '#163b5f', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
    </div>
    <div style={{ padding: '18px 20px', background: '#fff', borderRadius: '0 0 14px 14px' }}>
      {children}
    </div>
  </div>
);


/* ─────────────────────────────────────────────────────────────
   WitnessGate — mandatory Yes / No choice before witness UI
   witnessChoice: null | 'yes' | 'no'
───────────────────────────────────────────────────────────── */
const WitnessGate = ({ choiceField, witnessesField, formik, isS1, isPreview, idx, persons }) => {
  const getNested = (obj, path) => path.split('.').reduce((a, k) => {
    if (a == null) return undefined;
    const arrMatch = k.match(/^(\w+)\[(\d+)\]$/);
    if (arrMatch) return a[arrMatch[1]]?.[parseInt(arrMatch[2])];
    return a[k];
  }, obj);

  const witnessChoice = getNested(formik.values, choiceField);
  const witnesses = getNested(formik.values, witnessesField) || [];

  // Read-only / preview
  if (!isS1 || isPreview) {
    if (witnesses.length === 0) {
      return (
        <div style={{ color: '#6a8ca8', fontSize: 12, padding: '6px 0 4px' }}>
          No witnesses reported
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {witnesses.map((w, wIdx) => (
          <div key={wIdx}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#163b5f' }}>Witness {wIdx + 1}</span>
            </div>
            <WitnessBlock w={w} idx={wIdx} fieldPrefix={witnessesField} disabled formik={formik} />
          </div>
        ))}
      </div>
    );
  }

  // Choice not yet made
  if (witnessChoice === null || witnessChoice === undefined) {
    return (
      <div style={{
        border: '2px dashed #c2d5e3',
        borderRadius: 14,
        background: 'linear-gradient(135deg,#f8fafc,#eef3f7)',
        padding: '20px 22px',
        textAlign: 'center',
      }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', boxShadow: '0 4px 14px rgba(22,59,95,0.3)' }}>
          <FeatherIcon icon="users" size={20} color="#fff" />
        </div>
        <p style={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Were there any witnesses?</p>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 18px' }}>Someone who saw or was present during the incident</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => {
              formik.setFieldValue(choiceField, 'yes');
              // Pre-add one empty witness
              formik.setFieldValue(witnessesField, [{ firstName: '', middleName: '', lastName: '', address: '', phoneNo: '', email: '' }]);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '9px 22px', borderRadius: 9,
              background: 'linear-gradient(135deg,#163b5f,#3a6b98)',
              color: '#fff', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              boxShadow: '0 4px 12px rgba(22,59,95,0.3)',
              transition: 'all 0.18s',
            }}
          >
            <FeatherIcon icon="user-plus" size={14} />
            Yes — Add Witnesses
          </button>
          <button
            type="button"
            onClick={() => {
              formik.setFieldValue(choiceField, 'no');
              formik.setFieldValue(witnessesField, []);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '9px 22px', borderRadius: 9,
              background: '#fff', color: '#475569',
              border: '1.5px solid #cbd5e1', cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              transition: 'all 0.18s',
            }}
          >
            <FeatherIcon icon="user-x" size={14} />
            No Witnesses
          </button>
        </div>
      </div>
    );
  }

  // Choice = 'no'
  if (witnessChoice === 'no') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        border: '1.5px solid #e2e8f0',
        borderRadius: 11,
        background: '#f8fafc',
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1.5px solid #e2e8f0' }}>
          <FeatherIcon icon="users" size={15} color="#94a3b8" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>No witnesses</div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 1 }}>You indicated there were no witnesses present</div>
        </div>
        <button
          type="button"
          onClick={() => formik.setFieldValue(choiceField, null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 13px', borderRadius: 8,
            background: '#eef3f7', color: '#163b5f',
            border: '1.5px solid #c2d5e3', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, flexShrink: 0,
          }}
        >
          <FeatherIcon icon="refresh-cw" size={12} />
          Change
        </button>
      </div>
    );
  }

  // Choice = 'yes' — show witness list + add button
  return (
    <div>
      {witnesses.length === 0 ? (
        <div style={{ color: '#6a8ca8', fontSize: 12, padding: '6px 0 4px' }}>
          No witnesses added yet — use the button above to add one
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {witnesses.map((w, wIdx) => (
            <div key={wIdx}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#163b5f' }}>Witness {wIdx + 1}</span>
                <button type="button" className="pir-btn-danger-sm" style={{ padding: '2px 8px' }} onClick={() => {
                  const cur = [...witnesses];
                  cur.splice(wIdx, 1);
                  formik.setFieldValue(witnessesField, cur);
                  // If all removed, reset choice
                  if (cur.length === 0) formik.setFieldValue(choiceField, null);
                }}>
                  <FeatherIcon icon="trash-2" size={12} />
                </button>
              </div>
              <WitnessBlock w={w} idx={wIdx} fieldPrefix={witnessesField} disabled={false} formik={formik} />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="pir-btn-ghost"
          style={{ padding: '5px 14px', fontSize: 12 }}
          onClick={() => {
            formik.setFieldValue(witnessesField, [...witnesses, { firstName: '', middleName: '', lastName: '', address: '', phoneNo: '', email: '' }]);
          }}
        >
          <FeatherIcon icon="plus" size={12} /> Add Another Witness
        </button>
        <button
          type="button"
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 13px', borderRadius: 8,
            background: '#fff4f4', color: '#dc2626',
            border: '1.5px solid #fecaca', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
          }}
          onClick={() => {
            formik.setFieldValue(choiceField, null);
            formik.setFieldValue(witnessesField, []);
          }}
        >
          <FeatherIcon icon="x-circle" size={12} />
          Actually, no witnesses
        </button>
      </div>
    </div>
  );
};


const PersonForm = ({ idx, persons, isS1, isPreview, formik, attachProps, onSubmitReady }) => {
  const pf = (sub) => `notification.personsInvolved[${idx}].${sub}`;
  const pv = (sub) => {
    const parts = sub.split('.');
    let val = persons[idx];
    for (const p of parts) val = val?.[p];
    return val;
  };

  const personType = pv('type');
  const personSpecifyType = pv('specifyType');
  const typeLabel = (() => {
    if (!personType) return null;
    const base = PERSON_TYPE_LABELS[personType] || formatLabel(personType);
    if (personType === 'other' && personSpecifyType?.trim()) return `${base} (${personSpecifyType.trim()})`;
    return base;
  })();
  const fullName = [pv('firstName'), pv('lastName')].filter(Boolean).join(' ');
  const witnessCount = (pv('witnesses') || []).length;

  return (
    <div className="pir-person-card">
      <div className="pir-person-card-header">
        <div className="pir-person-card-avatar">
          {fullName ? fullName.charAt(0).toUpperCase() : <FeatherIcon icon="user" size={18} color="#fff" />}
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
          <div className="pir-person-card-name">{fullName || 'Your Details'}</div>
          {typeLabel && <div className="pir-person-card-type">{typeLabel}</div>}
        </div>
      </div>

      <div className="pir-person-card-body">

        {/* ── BOX 1: Your Information ── */}
        <SectionBox title="Your Information" icon="user">
          <div className="pir-form-row pir-form-row-3col">
            <div>
              <FL required>First Name</FL>
              <input className="pir-input" placeholder="e.g. John" value={pv('firstName') || ''} onChange={(e) => formik.setFieldValue(pf('firstName'), e.target.value)} disabled={!isS1 || isPreview} />
            </div>
            <div>
              <FL>Middle Name</FL>
              <input className="pir-input" placeholder="e.g. Michael" value={pv('middleName') || ''} onChange={(e) => formik.setFieldValue(pf('middleName'), e.target.value)} disabled={!isS1 || isPreview} />
            </div>
            <div>
              <FL required>Last Name</FL>
              <input className="pir-input" placeholder="e.g. Smith" value={pv('lastName') || ''} onChange={(e) => formik.setFieldValue(pf('lastName'), e.target.value)} disabled={!isS1 || isPreview} />
            </div>
          </div>

          <div className="pir-form-row pir-form-row-email-phone-notification-date">
            <div>
              <FL required>Email Address</FL>
              <input className="pir-input" type="email" placeholder="e.g. john@email.com" value={pv('email') || ''} onChange={(e) => formik.setFieldValue(pf('email'), e.target.value)} disabled={!isS1 || isPreview} />
            </div>
            <div>
              <FL required>Phone Number(s)</FL>
              {(pv('phoneNumbers') && pv('phoneNumbers').length > 0 ? pv('phoneNumbers') : [pv('phoneNo') || '']).map((phone, phIdx) => (
                <div key={phIdx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: phIdx < (pv('phoneNumbers') || ['']).length - 1 ? 6 : 0 }}>
                  <input className="pir-input" placeholder="(000) 000-0000" value={phone || ''}
                    onChange={(e) => {
                      const updated = [...(pv('phoneNumbers') || [''])];
                      updated[phIdx] = e.target.value;
                      formik.setFieldValue(pf('phoneNumbers'), updated);
                      if (phIdx === 0) formik.setFieldValue(pf('phoneNo'), e.target.value);
                    }}
                    disabled={!isS1 || isPreview}
                    style={{ flex: 1 }} />
                  {isS1 && !isPreview && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {(pv('phoneNumbers') || ['']).length > 1 && (
                        <button type="button" onClick={() => {
                          const updated = [...(pv('phoneNumbers') || [''])];
                          updated.splice(phIdx, 1);
                          formik.setFieldValue(pf('phoneNumbers'), updated.length > 0 ? updated : ['']);
                          formik.setFieldValue(pf('phoneNo'), (updated.length > 0 ? updated : [''])[0] || '');
                        }} style={{
                          width: 28, height: 28, borderRadius: 8, border: '1.5px solid rgba(239,68,68,0.3)',
                          background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                          fontWeight: 700, transition: 'all 0.15s', lineHeight: 1, padding: 0,
                        }} title="Remove phone number">−</button>
                      )}
                      {phIdx === (pv('phoneNumbers') || ['']).length - 1 && (
                        <button type="button" onClick={() => {
                          const updated = [...(pv('phoneNumbers') || [''])];
                          updated.push('');
                          formik.setFieldValue(pf('phoneNumbers'), updated);
                        }} style={{
                          width: 28, height: 28, borderRadius: 8, border: '1.5px solid rgba(22,59,95,0.25)',
                          background: 'rgba(22,59,95,0.06)', color: '#163b5f', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                          fontWeight: 700, transition: 'all 0.15s', lineHeight: 1, padding: 0,
                        }} title="Add phone number">+</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <FL required>Notified Property Manager On</FL>
              <DateTimePicker value={pv('notificationToPropertyManagerDateTime')} onChange={(d) => formik.setFieldValue(pf('notificationToPropertyManagerDateTime'), d)} disabled={!isS1 || isPreview} maxNow />
            </div>
          </div>

          <div className="pir-form-row pir-form-row-unit-addr">
            <div>
              <FL required>Unit No.</FL>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <input className="pir-input" placeholder="e.g. 204" value={pv('unitNo') === 'N/A' ? '' : pv('unitNo') || ''} onChange={(e) => formik.setFieldValue(pf('unitNo'), e.target.value)} disabled={!isS1 || isPreview || pv('unitNo') === 'N/A'} />
                </div>
                {isS1 && !isPreview && (
                  <label
                    className={`pir-pill ${pv('unitNo') === 'N/A' ? 'active' : ''}`}
                    style={{ marginBottom: 0, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={() => formik.setFieldValue(pf('unitNo'), pv('unitNo') === 'N/A' ? '' : 'N/A')}
                  >
                    N/A
                  </label>
                )}
              </div>
            </div>
            <div style={{ flex: 2 }}>
              <FL required>Home Address</FL>
              <AddressInput
                value={pv('homeAddress') || pv('address') || ''}
                onChange={(val) => {
                  formik.setFieldValue(pf('homeAddress'), val);
                  formik.setFieldValue(pf('address'), val);
                }}
                disabled={!isS1 || isPreview}
                placeholder="Start typing your address…"
              />
            </div>
          </div>
        </SectionBox>

        {/* ── BOX 2: Statement of Incident ── */}
        <SectionBox title="Statement of Incident" icon="file-text" accentColor="linear-gradient(135deg,#163b5f,#6a8ca8)">

          {/* Statement textarea */}
          <div style={{ marginBottom: 4 }}>
            <FL required>Describe what happened (<small>Include who, what, when, where, why and how — provide as much detail as possible</small>)</FL>
            <textarea
              className="pir-input pir-textarea"
              rows={5}
              placeholder="e.g. On May 5th at 3:00 PM, a water leak from the unit above caused damage to the ceiling in the living room…"
              value={pv('statement') || ''}
              onChange={(e) => formik.setFieldValue(pf('statement'), e.target.value)}
              disabled={!isS1 || isPreview}
            />
          </div>

          {/* ── Attachments divider ── */}
          <SubDivider icon="paperclip" label="Attachments" />
          <AttachmentsRow
            {...attachProps}
            field={`notification.personsInvolved[${idx}].attachments`}
            choiceField={`notification.personsInvolved[${idx}].attachmentChoice`}
          />

          {/* ── Witnesses divider ── */}
          <SubDivider icon="users" label={witnessCount > 0 ? `Witnesses (${witnessCount})` : 'Witnesses'} />

          <WitnessGate
            choiceField={pf('witnessChoice')}
            witnessesField={pf('witnesses')}
            formik={formik}
            isS1={isS1}
            isPreview={isPreview}
            idx={idx}
            persons={persons}
          />

          {/* ── Signature divider ── */}
          <SubDivider icon="edit-3" label="Your Signature">
            <span style={{ color: '#ef4444', fontSize: 12, marginLeft: 2 }}>*</span>
          </SubDivider>
          <SignaturePad
            value={pv('signature.signature') || ''}
            date={pv('signature.date') || ''}
            onChange={(data) => formik.setFieldValue(pf('signature.signature'), data)}
            onDateChange={(dateStr) => formik.setFieldValue(pf('signature.date'), dateStr)}
            disabled={!isS1 || isPreview}
          />

        </SectionBox>

        {isS1 && !isPreview && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12, marginTop: 4 }}>
            <button type="button" className="pir-review-btn" onClick={onSubmitReady}>
              <span className="pir-review-btn-icon"><FeatherIcon icon="eye" size={15} /></span>
              <span>Review & Submit Report</span>
              <span className="pir-review-btn-arrow"><FeatherIcon icon="arrow-right" size={15} /></span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


const GenerateIncidentReportForm = ({
  formik, onSubmit, isSubmitting, token, mode = 'initial',
  tokenInfo = {}, propertyTitle, invitedBy, expiresAt, referenceId,
  companyLogo,
  visibleSections = [1], alreadyConfirmedBanner = false,
}) => {
  const isS1 = mode === 'initial';
  const isPreview = mode === 'preview' || alreadyConfirmedBanner;
  const [uploadingCount, setUploadingCount] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const uploadingCountRef = useRef(0);

  const handleUploadingChange = useCallback((delta) => {
    uploadingCountRef.current = Math.max(0, uploadingCountRef.current + delta);
    setUploadingCount(uploadingCountRef.current);
  }, []);

  const isUploadingFiles = uploadingCount > 0;
  const fv = formik.values;
  const n = fv.notification || {};
  const persons = n.personsInvolved || n.personInvolved || [];

  const handleSubmitReady = async () => {
    if (isUploadingFiles) { toast.info('Please wait for uploads to complete.'); return; }
    const isValid = await validatePublicIncidentReport(formik.values);
    if (!isValid) return;
    setShowPreviewModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (localSubmitting) return;
    setLocalSubmitting(true);
    formik.setSubmitting(true);
    try {
      await onSubmit();
    } catch (err) {
      toast.error(err.message || 'Failed to submit report');
    } finally {
      setShowPreviewModal(false);
      setLocalSubmitting(false);
      formik.setSubmitting(false);
    }
  };

  const attachProps = { formik, token, mode, onUploadingChange: handleUploadingChange };

  return (
    <div className="pir-wrap">

      <div className="pir-header">
        <div className="pir-header-glow" />
        <div className="pir-header-inner">
          <div className="pir-header-top">
            <div>
              <h1 className="pir-header-title">Incident Report</h1>
              <p className="pir-header-sub">{propertyTitle ? `Property: ${propertyTitle}` : 'Please complete all required fields accurately'}</p>
            </div>
            <img src="/images/rentdigicare-logo.svg" alt="RentDigiCare" className="pir-header-logo" />
          </div>
          <div className="pir-badge-row">
            {expiresAt && (
              <span className="pir-badge pir-badge-amber">
                <FeatherIcon icon="clock" size={11} />
                Due: {new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {(n?.managerReporting || n?.managerEmail || n?.managerPhoneNo) && (
              <span className="pir-badge">
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                  {n?.managerReporting && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FeatherIcon icon="user" size={11} />
                      Manager: {n.managerReporting}
                    </div>
                  )}
                  {n?.managerEmail && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FeatherIcon icon="mail" size={11} />
                      {n.managerEmail}
                    </div>
                  )}
                  {n?.managerPhoneNo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FeatherIcon icon="phone" size={11} />
                      {n.managerPhoneNo}
                    </div>
                  )}
                </div>
              </span>
            )}
          </div>
        </div>
      </div>

      {alreadyConfirmedBanner && (
        <div style={{ background: 'linear-gradient(135deg,#d1fae5,#a7f3d0)', border: '1.5px solid #34d399', borderRadius: 14, padding: '16px 22px', margin: '16px auto', maxWidth: 860, display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 4px 16px rgba(5,150,105,0.12)' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}>
            <FeatherIcon icon="check-circle" size={22} color="#fff" />
          </div>
          <div>
            <strong style={{ color: '#065f46', fontSize: 15, fontWeight: 700 }}>Report already submitted</strong>
            <p style={{ margin: 0, fontSize: 12.5, color: '#047857', marginTop: 3 }}>This form is displayed in read-only mode. No further changes can be made.</p>
          </div>
        </div>
      )}

      <div className="pir-content">
        {isUploadingFiles && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12, gap: 6, fontSize: 12, color: '#d97706', fontWeight: 600 }}>
            <Spinner size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
            Uploading files…
          </div>
        )}

        {persons.map((_p, idx) => (
          <PersonForm
            key={idx} idx={idx} persons={persons} isS1={isS1} isPreview={isPreview}
            formik={formik} attachProps={attachProps}
            onSubmitReady={handleSubmitReady}
          />
        ))}
      </div>

        <PreviewModal
          isOpen={showPreviewModal}
          toggle={() => !localSubmitting && setShowPreviewModal(false)}
          persons={persons}
          onConfirmSubmit={handleConfirmSubmit}
          isSubmitting={localSubmitting}
          token={token}
        />

        {companyLogo && (
          <div style={{ 
            marginTop: '40px', 
            paddingBottom: '40px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            opacity: 0.8
          }}>
            <div style={{ height: '1px', width: '60px', background: 'rgba(22,59,95,0.1)' }} />
            <img 
              src={companyLogo} 
              alt="Company Logo" 
              style={{ 
                maxHeight: '34px', 
                maxWidth: '160px', 
                objectFit: 'contain',
                filter: 'grayscale(0.4) opacity(0.7)'
              }} 
            />
          </div>
        )}
      </div>
    );
};

export { GenerateIncidentReportForm };